import {
  Component,
  OnChanges,
  Input,
  Output,
  EventEmitter,
  ViewChild,
  ElementRef,
  ViewEncapsulation,
  HostBinding,
  NgZone,
  ChangeDetectionStrategy,
  ContentChild,
  ChangeDetectorRef,
  SimpleChanges,
  Renderer2,
  HostListener,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

import { PgLabelDirective } from '../pg-content-blocks/pg-label/pg-label.directive';
import { END, HOME, LEFT_ARROW, RIGHT_ARROW } from '@angular/cdk/keycodes';
import { HammerInput } from '../core/events/gestures-annotations';

export class Tick {
  constructor(
    public index: number,
    public value?: number,
    public tickLocation?: number
  ) {}
}

export class sliderValueChange {
  constructor(public source?: PgSliderComponent, public tickedValue?: number) {}
}

@Component({
  selector: 'pg-slider, grit-slider',
  templateUrl: './pg-slider.component.html',
  styleUrls: ['./pg-slider.component.scss'],
  exportAs: 'gritSlider',
  encapsulation: ViewEncapsulation.None,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: PgSliderComponent,
      multi: true,
    },
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PgSliderComponent implements ControlValueAccessor, OnChanges {
  public _min: number = 3;
  public _max: number = 12;
  public _step: number = 1;
  public _onDraggingTickValue: Tick | null;
  public _dragging = false;
  public _currentValue: number | null = 0;
  public currentTick: Tick | null = null;

  private _ticks: Array<Tick>;

  private _dragPercentage: number;
  private _sliderBarWidth: number;
  private _thumbLabelSize: number;

  @Input('disabled')
  @HostBinding('class.disabled')
  @HostBinding('attr.aria-disabled')
  disabled = false;

  @HostBinding('attr.role') role = 'slider';

  @HostListener('window:resize', ['$event'])
  onResize(event) {
    this._generateTicks();
    this._initializeDragProperties();
    this.resizeAndPositionElementsOnWindowResize();
  }

  @HostBinding('class.is-focused')
  _isFocused = false;

  @Input('min')
  @HostBinding('attr.aria-valuemin')
  get min(): number {
    return this._min;
  }

  set min(value) {
    if (value !== this._min) {
      this._min = value;
      // if (this.value === null) {
      //   this.value = this._min;
      // }
    }
  }

  @Input('max')
  @HostBinding('attr.aria-valuemax')
  get max(): number {
    return this._max;
  }

  set max(value) {
    if (value !== this._max) {
      this._max = value;
    }
  }

  @Input('step')
  get step(): number {
    return this._step;
  }

  set step(value) {
    if (value !== this._step) {
      this._step = value;
    }
  }

  @Input()
  @HostBinding('attr.aria-valuenow')
  get value(): number | null {
    // if (this._currentValue === null) {
    //   this._currentValue = this._min;
    // }
    return this._currentValue || this._min;
  }

  set value(value: number | null) {
    // if (value !== this._currentValue || (!value && !this._currentValue)) {
    this._currentValue = value || this._min;

    if (!this.changeRef['destroyed']) {
      this.changeRef.detectChanges();
    }

    if (this._ticks /*&& this.currentTick.value !== value*/) {
      this.currentTick = this._ticks.find(
        tick => tick.value === this._currentValue
      );
      this._onDraggingTickValue = this.currentTick;
      this._ngZone.runOutsideAngular(() =>
        setTimeout(() => {
          this.thumbPositionChange(this.currentTick);
        })
      );
      // }

      this.onChange(this._currentValue);
    }
  }

  //event emmited when the slider val changes
  @Output() readonly change: EventEmitter<sliderValueChange> = new EventEmitter<
    sliderValueChange
  >();

  // Event emitted when the slider thumb moves.
  // replicating native functionality.
  @Output() readonly dragChange: EventEmitter<
    sliderValueChange
  > = new EventEmitter<sliderValueChange>();

  @ContentChild(PgLabelDirective, { static: false })
  label: PgLabelDirective;

  @ViewChild('thumb', { static: true })
  _thumb: ElementRef;
  @ViewChild('slideBarNonFilled', { static: true }) _sliderBar: ElementRef;
  @ViewChild('sliderBarFill', { static: true }) _sliderBarFill: ElementRef;
  @ViewChild('thumbLabel', { static: true }) _thumbLabel: ElementRef;

  constructor(
    public _ngZone: NgZone,
    public _elementRef: ElementRef,
    public changeRef: ChangeDetectorRef,
    public renderer: Renderer2
  ) {}

  ngOnChanges(changes: SimpleChanges) {
    if (!changes.isFirstChange && !changes.disabled) {
      // this.value = this.min;
    }

    this._generateTicks();
  }

  ngOnInit(): void {
    this._generateTicks();
  }

  onChange = _ => {};
  onTouch = () => {};

  focus() {
    this._isFocused = true;
  }

  onBlur() {
    this._isFocused = false;
  }

  _onDragStart() {
    // evnt.preventDefault();
    if (!this._dragging && !this.disabled) {
      this._dragging = true;
      this.focus();
    }
  }

  resizeAndPositionElementsOnWindowResize() {
    if (!this.disabled) {
      this._dragPercentage = this._getDragPercentage(0);
      let newThumbMoveLocation: number =
        (this._dragPercentage / 100) * this._sliderBarWidth;
      this._thumb.nativeElement.style.transform = `translateX(${newThumbMoveLocation}px)`;
      this._sliderBarFill.nativeElement.style.width = `${newThumbMoveLocation +
        10}px`;
      let labelPosition = newThumbMoveLocation - this._thumbLabelSize / 2 + 10;
      this._thumbLabel.nativeElement.style.transform = `translateX(${labelPosition}px)`;
    }
  }
  _onDrag(evnt: HammerInput) {
    evnt.preventDefault();

    if (this._dragging && !this.disabled) {
      this._dragPercentage = this._getDragPercentage(evnt.deltaX);
      let closestTick: Tick;

      let newThumbMoveLocation: number =
        (this._dragPercentage / 100) * this._sliderBarWidth;

      this._thumb.nativeElement.style.transform = `translateX(${newThumbMoveLocation}px)`;
      this._sliderBarFill.nativeElement.style.width = `${newThumbMoveLocation +
        10}px`;
      let labelPosition = newThumbMoveLocation - this._thumbLabelSize / 2 + 10;
      this._thumbLabel.nativeElement.style.transform = `translateX(${labelPosition}px)`;

      closestTick = this._getCloserValue(this._dragPercentage);

      this._onDraggingTickValue = closestTick;
      if (closestTick !== this._onDraggingTickValue && closestTick) {
        this._emitInputEvent();
      }
    }
  }

  _getCloserValue(goal: number): Tick {
    let slidePercentage = goal / 100;
    let pos = (this._ticks.length - 1) * slidePercentage;
    let halfWay = false;
    if (pos % 1 > 0.5) {
      halfWay = true;
    }
    let exactPos = halfWay ? Math.ceil(pos) : Math.floor(pos);
    return this._ticks[exactPos];
  }

  _changeIncrement(changeDirection: number) {
    this.currentTick = this._ticks[this.currentTick.index + changeDirection];
    this._onDraggingTickValue = this.currentTick;

    this._ngZone.runOutsideAngular(() =>
      setTimeout(() => {
        this.thumbPositionChange(this.currentTick);
      })
    );
  }

  _onDragEnd() {
    if (this._dragging) {
      this._dragging = false;
      this.currentTick = this._onDraggingTickValue;
      this._emitChangeEvent();
    }
  }
  _onClickThumb(evnt: MouseEvent) {
    evnt.stopPropagation();
  }

  _onMousedown(event: MouseEvent) {
    if (this.disabled || event.button !== 0 || !this._ticks) {
      return;
    }
    this._dragging = false;
    this.focus();

    let distance = event.offsetX;
    let clickedPercentage = (distance / this._sliderBarWidth) * 100;
    clickedPercentage = Math.max(0, Math.min(clickedPercentage, 100));
    let closestTick = this._ticks.reduce(function(prev, curr) {
      return Math.abs(curr.tickLocation - clickedPercentage) <
        Math.abs(prev.tickLocation - clickedPercentage)
        ? curr
        : prev;
    });

    if (closestTick !== this.currentTick) {
      this._writeInitialTickValue(closestTick);
      this.thumbPositionChange(closestTick);
      this._emitInputEvent();
      this._emitChangeEvent();
    }
  }

  _onKeydown(event: KeyboardEvent) {
    if (this.disabled) {
      return;
    }
    const oldValue = this.value;

    switch (event.keyCode) {
      case END:
        this.value = this.max;
        break;
      case HOME:
        this.value = this.min;
        break;
      case LEFT_ARROW:
        if (this.currentTick.value !== this.min) {
          this._changeIncrement(-1);
        }
        break;
      case RIGHT_ARROW:
        if (this.currentTick.value !== this.max) {
          this._changeIncrement(1);
        }
        break;
      default:
        return;
    }

    if (oldValue !== this.currentTick.value) {
      this._emitInputEvent();
      this._emitChangeEvent();
    }
  }

  _onKeyup() {
    this._dragging = false;
  }

  _initializeDragProperties() {
    const thumbEl = this._thumb.nativeElement;
    this._thumbLabelSize = this._thumbLabel.nativeElement.offsetWidth;
    this._sliderBarWidth =
      this._sliderBar.nativeElement.clientWidth - thumbEl.clientWidth;
  }

  writeValue(value: any): void {
    this.value = value;
  }

  registerOnChange(fn: any) {
    this.onChange = fn;
    this.onChange(this.value);
  }

  registerOnTouched(fn: any): void {
    this.onTouch = fn;
  }

  private thumbPositionChange(currentTick: Tick) {
    if (!this.currentTick) {
      return;
    }
    if (!this._sliderBarWidth || !this._thumbLabelSize) {
      this._initializeDragProperties();
    }

    let newThumbMoveLocation: number =
      (currentTick.tickLocation / 100) * this._sliderBarWidth;
    this._thumb.nativeElement.style.transform = `translateX(${newThumbMoveLocation}px)`;
    this._sliderBarFill.nativeElement.style.width = `${newThumbMoveLocation +
      10}px`;
    let labelPosition = newThumbMoveLocation - this._thumbLabelSize / 2 + 10;
    this._thumbLabel.nativeElement.style.transform = `translateX(${labelPosition}px)`;
  }

  private _getDragPercentage(distance: number): number {
    let currentDragDistance =
      (this.currentTick.tickLocation / 100) * this._sliderBarWidth;
    let percentage =
      ((distance + currentDragDistance) / this._sliderBarWidth) * 100;
    return Math.max(0, Math.min(percentage, 100));
  }

  public _generateTicks(): void {
    this._ticks = [];
    let min = this.min;

    let index = 0;
    for (this.min; min <= this.max - 1; min += this.step) {
      let tick = new Tick(
        index,
        min,
        this._generateTickLocation(this._ticks.length)
      );

      this._ticks.push(tick);

      if (tick.value === this.value) {
        this._writeInitialTickValue(tick);
      }
      index++;
    }

    if (this._ticks[this._ticks.length - 1].value !== this.max) {
      let tick = new Tick(index, this.max, 100);
      this._ticks.push(tick);
    }
    //index = 0;
  }

  private _generateTickLocation(tick): number {
    let tickLocation: number =
      (100 / ((this.max - this.min) / this.step)) * tick;
    return tickLocation;
  }

  private _writeInitialTickValue(tick: Tick) {
    this.currentTick = tick;
    this._onDraggingTickValue = this.currentTick;
  }

  private _emitChangeEvent() {
    this.onChange(this.currentTick.value);
    this.change.emit(this._genChangeEvent());
    this.writeValue(this.currentTick.value);
  }

  private _emitInputEvent() {
    this.dragChange.emit(this._genChangeEvent());
  }

  private _genChangeEvent(value = this.value): sliderValueChange {
    let event = new sliderValueChange();

    event.source = this;
    event.tickedValue = value;

    return event;
  }
}
